import React, { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { PortalConfig, ClientProjectAccess } from '../../../types';
import { getPortalConfig, createPortalConfig, updatePortalConfig, getClientAccess } from '../../../services/portalService';

interface Props { pca: IPublicClientApplication; projectId: string; }

const SECTIONS = ['tasks', 'issues', 'milestones', 'documents', 'goals'];

const thStyle: React.CSSProperties = {
  fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 11,
  color: 'var(--ink-3)', letterSpacing: '0.08em', textTransform: 'uppercase',
  padding: '8px 12px', borderBottom: '2px solid var(--border)', textAlign: 'left',
};
const tdStyle: React.CSSProperties = {
  fontFamily: 'var(--font-ui)', fontWeight: 400, fontSize: 14,
  color: 'var(--ink)', padding: '10px 12px',
};

export default function ClientPortalTab({ pca, projectId }: Props) {
  const [config, setConfig]   = useState<PortalConfig | null>(null);
  const [access, setAccess]   = useState<ClientProjectAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [sections, setSections] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState<string | null>(null);

  const reload = () => Promise.all([getPortalConfig(pca, projectId), getClientAccess(pca, projectId)])
    .then(([c, a]) => {
      setConfig(c); setAccess(a);
      if (c) {
        setEnabled(c.dagrun_pm_isenabled);
        try { setSections(c.dagrun_pm_sectionsconfigjson ? JSON.parse(c.dagrun_pm_sectionsconfigjson) : {}); } catch { setSections({}); }
      }
    })
    .finally(() => setLoading(false));
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  const handleEnable = async () => {
    setLoading(true);
    await createPortalConfig(pca, projectId);
    reload();
  };

  const handleSave = async () => {
    if (!config) return;
    await updatePortalConfig(pca, config.dagrun_pm_portalconfigid, {
      dagrun_pm_isenabled: enabled,
      dagrun_pm_sectionsconfigjson: JSON.stringify(sections),
    });
    setMsg('Saved');
    reload();
  };

  if (loading) return <div style={{ padding: '24px 32px' }}><div className="spinner" /></div>;

  if (!config) return (
    <div style={{ padding: '24px 32px' }}>
      <div className="card" style={{ padding: 48, textAlign: 'center', maxWidth: 560, margin: '40px auto' }}>
        <div className="eyebrow">CLIENT PORTAL</div>
        <h3 style={{ fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 16, color: 'var(--ink-2)', marginTop: 12 }}>
          Client Portal not configured
        </h3>
        <p style={{ fontWeight: 300, fontSize: 14, color: 'var(--ink-3)', marginTop: 8 }}>
          Share read-only project updates with your client through a branded Power Pages portal.
        </p>
        <button className="btn-primary" style={{ marginTop: 16 }} onClick={handleEnable}>Enable Portal</button>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 760 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3>Client Portal</h3>
      </div>

      {/* Config */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>CONFIGURATION</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          Portal Enabled
        </label>
        <div className="eyebrow" style={{ marginTop: 16, marginBottom: 8 }}>VISIBLE SECTIONS</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {SECTIONS.map(s => (
            <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 400, textTransform: 'capitalize' }}>
              <input type="checkbox" checked={!!sections[s]} onChange={e => setSections({ ...sections, [s]: e.target.checked })} />
              Show {s}
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <button className="btn-primary" onClick={handleSave}>Save</button>
          {msg && <span style={{ color: 'var(--success)', fontSize: 13 }}>{msg}</span>}
        </div>
      </div>

      {/* Access */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span className="eyebrow">CLIENT ACCESS ({access.length})</span>
          <button className="btn-secondary" onClick={() => alert('Portal invitations require Power Pages to be deployed. Contact your tenant admin.')}>+ Invite Client</button>
        </div>
        {access.length === 0 ? (
          <div style={{ fontWeight: 300, fontSize: 14, color: 'var(--ink-3)' }}>No client access granted yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thStyle}>Contact</th>
              <th style={thStyle}>Granted On</th>
              <th style={thStyle}>Granted By</th>
            </tr></thead>
            <tbody>
              {access.map(a => (
                <tr key={a.dagrun_pm_clientprojectaccessid} style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={tdStyle}>{a['_dagrun_pm_contactid_value@OData.Community.Display.V1.FormattedValue'] ?? a._dagrun_pm_contactid_value}</td>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)' }}>{a.dagrun_pm_grantedon ? new Date(a.dagrun_pm_grantedon).toLocaleDateString() : '—'}</td>
                  <td style={tdStyle}>{a.dagrun_pm_grantedby ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
